import { StorefrontOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { RoutesList } from '../../../config/routes-list';
import type { Supplier } from '../../../services/goods-receiving';
import type { DeliveryNoteDuplicateSummary } from '../../../services/goods-receiving/goods-receiving.types';
import { DuplicateDeliveryDialog } from '../components/duplicate-delivery-dialog';
import { SupplierAutocomplete } from '../components/supplier-autocomplete';
import { SupplierPhotoExtraction } from '../components/supplier-photo-extraction';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepHeader } from '../components/wizard-step-header';
import { useCheckDuplicate, usePatchDraft } from '../hooks/use-draft-mutations';
import { useSuppliers } from '../hooks/use-suppliers';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { WizardLayout } from './wizard-layout';

const LAST_SUPPLIER_KEY = 'goodsReceiving.lastSupplierId';

function loadLastSelectedSupplier(): string | null {
  try {
    return localStorage.getItem(LAST_SUPPLIER_KEY);
  } catch {
    return null;
  }
}

function saveLastSelectedSupplier(supplierId: string): void {
  try {
    localStorage.setItem(LAST_SUPPLIER_KEY, supplierId);
  } catch {
    // ignore
  }
}

function isSupplierInList(supplierId: string, suppliers: Supplier[]): Supplier | null {
  return suppliers.find((s) => s.id === supplierId) ?? null;
}

export function SupplierStepPage() {
  const { t } = useTranslation('goodsReceiving');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { data: draft } = useWizardDraft(draftId);
  const patchDraft = usePatchDraft();
  const checkDuplicate = useCheckDuplicate();
  const { data: suppliers = [] } = useSuppliers();

  const [duplicates, setDuplicates] = useState<DeliveryNoteDuplicateSummary[]>([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  // Tracks the fingerprint of values already checked so we don't re-fire on every render.
  const lastCheckedFingerprintRef = useRef<string | null>(null);

  // Track selected supplier ID in local state; null-safe access for nullable draft.supplier
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(
    draft?.supplier?.id ?? null,
  );

  // Sync when draft loads (for resume)
  useEffect(() => {
    if (draft?.supplier?.id) {
      setSelectedSupplierId(draft.supplier.id);
    }
  }, [draft?.supplier?.id]);

  // Auto-select last used supplier if draft has none
  useEffect(() => {
    if (selectedSupplierId !== null || suppliers.length === 0) {
      return;
    }
    const lastId = loadLastSelectedSupplier();
    if (lastId && isSupplierInList(lastId, suppliers)) {
      setSelectedSupplierId(lastId);
    }
  }, [suppliers, selectedSupplierId]);

  const selectedSupplier =
    selectedSupplierId != null
      ? (suppliers.find((s) => s.id === selectedSupplierId) ?? null)
      : null;

  const handleSupplierChange = useCallback((supplier: Supplier | null) => {
    if (!supplier) {
      return;
    }
    setSelectedSupplierId(supplier.id);
    saveLastSelectedSupplier(supplier.id);
  }, []);

  const [isExtracting, setIsExtracting] = useState(false);
  const supplierInputRef = useRef<HTMLInputElement>(null);

  // Auto-fire duplicate check when the AI extraction finishes and the draft has
  // all three matching fields (supplier + documentNumber + date).
  useEffect(() => {
    if (!draftId) {
      return;
    }
    if (draft?.extractionStatus !== 'COMPLETED') {
      return;
    }
    const supplierId = draft?.supplier?.id ?? null;
    const documentNumber = draft?.documentNumber ?? null;
    const date = draft?.date ?? null;
    if (!supplierId || !documentNumber || !date) {
      return;
    }
    const fingerprint = `${supplierId}|${documentNumber}|${date}`;
    if (lastCheckedFingerprintRef.current === fingerprint) {
      return;
    }
    lastCheckedFingerprintRef.current = fingerprint;
    checkDuplicate.mutate(draftId, {
      onSuccess: (result) => {
        if (result.duplicates.length > 0) {
          setDuplicates(result.duplicates);
          setDuplicateDialogOpen(true);
        }
      },
    });
  }, [
    draftId,
    draft?.extractionStatus,
    draft?.supplier?.id,
    draft?.documentNumber,
    draft?.date,
    checkDuplicate,
  ]);

  const handleNext = () => {
    if (!selectedSupplierId || !draftId) {
      return;
    }
    patchDraft.mutate(
      { id: draftId, data: { supplierId: selectedSupplierId, currentStep: 'document' } },
      {
        onSuccess: () =>
          navigate(`${getStepRoute('document')}?draftId=${draftId}`, { flushSync: true }),
      },
    );
  };

  const handleBack = () => {
    navigate(RoutesList.GOODS_RECEIVING, { flushSync: true });
  };

  const canGoNext = !!selectedSupplierId;

  return (
    <WizardLayout>
      <Stack spacing={2.5}>
        <WizardStepHeader
          step="supplier"
          icon={<StorefrontOutlined />}
          title={t('supplierTitle')}
          subtitle={t('supplierSubtitle')}
        />

        <SupplierAutocomplete
          value={selectedSupplier}
          onChange={handleSupplierChange}
          error={false}
          inputRef={supplierInputRef}
        />

        <SupplierPhotoExtraction
          draftId={draftId}
          extractionStatus={draft?.extractionStatus}
          extractionResult={draft?.extractionResult}
          supplier={draft?.supplier}
          onSupplierSelected={handleSupplierChange}
          onLoadingChange={setIsExtracting}
          onRequestSupplierListFocus={() => supplierInputRef.current?.focus()}
        />
      </Stack>
      <WizardNavigation
        canGoBack={true}
        canGoNext={canGoNext}
        onBack={handleBack}
        onNext={handleNext}
        isLoading={isExtracting || patchDraft.isPending}
      />
      <DuplicateDeliveryDialog
        open={duplicateDialogOpen}
        duplicates={duplicates}
        onClose={() => setDuplicateDialogOpen(false)}
        onProceed={() => setDuplicateDialogOpen(false)}
      />
    </WizardLayout>
  );
}
